import {naturalSortByKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {redirectForDownload, setWindowTitle} from 'BenchlingUtil';
import {FONT_PROXIMA_10} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {Header1} from 'ui-library/Headers';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {confirm} from 'util/Modals';
import {capitalize} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';

import CreateInstrumentGatewayModal from './CreateInstrumentGatewayModal';
import {
  GATEWAY_TYPE_TO_DISPLAY,
  GatewayStatus,
  GatewayType,
  InstrumentGateway,
  LATEST_BASE_GATEWAY_INSTALLER_VERSION,
} from './InstrumentGatewayConstants';
import {
  archiveGateway,
  createGateway,
  getInstaller,
  listGateways,
  listPendingUpgradesForGateway,
  InstrumentGatewayUpgrade,
  createInstallerTokenFile,
  getGatewayInstaller,
  getS3GatewayInstaller,
} from './InstrumentGatewayDAO';
import {CreateS3GatewayModal, ViewBucketInfoModal} from './S3GatewayModal';
import UpgradeGatewayModal from './UpgradeGatewayModal';

interface GatewayCellProps {
  gateway: InstrumentGateway;
}

function DownloadInstallerCell(props: GatewayCellProps): JSX.Element {
  return (
    <Cell>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        onClick={async () => {
          const {downloadUrl} = await getInstaller(props.gateway.id);
          redirectForDownload(downloadUrl);
        }}
        disabled={props.gateway.status !== GatewayStatus.UNREGISTERED || props.gateway.isArchived}
        icon="download"
      />
    </Cell>
  );
}

function ArchiveCell(props: GatewayCellProps & {refetch: () => void}): JSX.Element {
  return (
    <Cell>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        onClick={() => {
          archiveGatewayWithMessage(props.gateway.id, props.refetch);
        }}
        icon="archive"
        disabled={props.gateway.isArchived}
      />
    </Cell>
  );
}

function archiveGatewayWithMessage(gatewayId: string, refetch: () => void): void {
  const msg =
    'Archiving this gateway will archive all connections associated with it and is not reversible. To re-connect to the instrument, you will need to install a new gateway and create new connections.';
  confirm('Archive Gateway?', msg, ButtonStyle.WARNING, {
    acceptText: 'Archive Gateway',
    rejectText: 'Cancel',
    promiseCompatible: true,
    onSubmit: () => {
      withLoadingScreen(archiveGateway(gatewayId), 'Archiving gateway...').then(() => {
        refetch();
      });
    },
  });
}

function UpgradeCell(props: GatewayCellProps & {refetch: () => void}): JSX.Element {
  const [pendingUpgrades, setPendingUpgrades] = useState([] as Array<InstrumentGatewayUpgrade>);
  const refetch = (): void => {
    listPendingUpgradesForGateway(props.gateway.id).then(({pendingUpgrades}) => {
      setPendingUpgrades(pendingUpgrades);
    });
  };
  useOnMount(refetch);
  return (
    <Cell>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        onClick={() =>
          runModal(UpgradeGatewayModal, {
            gatewayId: props.gateway.id,
            onRequestClose: refetch,
          })
        }
        disabled={pendingUpgrades.length === 0}
        icon="download"
      />
    </Cell>
  );
}

interface DownloadGatewaytModalProps {
  onRequestClose: () => void;
}

function DownloadGatewayModal(props: DownloadGatewaytModalProps): JSX.Element {
  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.TERTIARY_GRAY,
          text: 'Generate installer token',
          onClick: async () => {
            const {downloadUrl} = await createInstallerTokenFile();
            redirectForDownload(downloadUrl);
          },
        },
        {
          style: ButtonStyle.PRIMARY,
          cancel: true,
          text: 'Done',
        },
      ]}
      title="Download gateway"
      onRequestClose={props.onRequestClose}
      closeable={true}
    >
      <div className={css(styles.infoBanner)}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={`The latest version (${LATEST_BASE_GATEWAY_INSTALLER_VERSION}) will be downloaded.`}
        />
      </div>
      The gateway installer can be found in your browser downloads folder. Please additionally download an
      installer token by clicking on the "Generate installer token" button below.
      <br />
      <br />
      During the gateway installation process, you will be prompted to upload this installer token which
      expires in 1 hour.
      <br />
      <br />
      If a new token file needs to be generated once this modal has been closed, you can generate a new token
      from the "Download" dropdown.
    </Modal>
  );
}

function DownloadTokenModal(props: DownloadGatewaytModalProps): JSX.Element {
  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          cancel: true,
          text: 'Done',
        },
      ]}
      title="Generate new installer token"
      onRequestClose={props.onRequestClose}
      closeable={true}
    >
      The installer token file can be found in your browser downloads folder.
      <br />
      <br />
      During the gateway installation process, you will be prompted to upload an installer token which expires
      in 1 hour.
    </Modal>
  );
}

const GATEWAY_STATUS_TO_STYLE: {[status in GatewayStatus]: keyof typeof styles} = {
  [GatewayStatus.HEALTHY]: 'healthy',
  [GatewayStatus.UNHEALTHY]: 'unhealthy',
  [GatewayStatus.UNREGISTERED]: 'unregistered',
};

const GATEWAY_STATUS_TO_TEXT: {[status in GatewayStatus]: string} = {
  [GatewayStatus.HEALTHY]: 'Open',
  [GatewayStatus.UNHEALTHY]: 'Error',
  [GatewayStatus.UNREGISTERED]: 'Pending install',
};

const GATEWAY_STATUS_TO_ICON: {[status in GatewayStatus]: BenchlingIconName} = {
  [GatewayStatus.HEALTHY]: 'status-success',
  [GatewayStatus.UNHEALTHY]: 'status-failed',
  [GatewayStatus.UNREGISTERED]: 'request-in-progress',
};

function StatusCell(props: GatewayCellProps): JSX.Element {
  return (
    <Cell>
      <BenchlingIcon
        className={css(styles.statusIcon, styles[GATEWAY_STATUS_TO_STYLE[props.gateway.status]])}
        icon={GATEWAY_STATUS_TO_ICON[props.gateway.status]}
      />
      {capitalize(GATEWAY_STATUS_TO_TEXT[props.gateway.status])}
    </Cell>
  );
}

function VersionCell(props: GatewayCellProps): JSX.Element {
  const installerVersion = props.gateway.installerVersion;
  return (
    <Cell>
      {installerVersion}
      {installerVersion == null ? (
        'Unknown'
      ) : installerVersion === LATEST_BASE_GATEWAY_INSTALLER_VERSION ||
        props.gateway.gatewayType === GatewayType.S3 ? (
        ' (Latest)'
      ) : (
        <Tooltip placement={TooltipPlacement.AUTO} title="A new installer version is available to download.">
          <BenchlingIcon className={css(styles.versionWarningIcon)} icon="status-warning" />
        </Tooltip>
      )}
    </Cell>
  );
}

function ActionCell(props: GatewayCellProps & {refetch: () => void}): JSX.Element {
  return (
    <Cell>
      <span>
        <Dropdown
          closeOnInsideClick={true}
          closeOnOutsideClick={true}
          menu={<ActionDropdownMenu {...props} />}
        >
          <Button
            buttonStyle={ButtonStyle.TRANSPARENT}
            hideDropdownCaret={true}
            icon="overflow"
            size={ButtonSize.SMALL}
          />
        </Dropdown>
      </span>
    </Cell>
  );
}

function ActionDropdownMenu(props: GatewayCellProps & {refetch: () => void}): JSX.Element {
  return (
    <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM}>
      <Section>
        <Option
          onSelect={() => {
            archiveGatewayWithMessage(props.gateway.id, props.refetch);
          }}
          isDisabled={props.gateway.isArchived}
        >
          Archive gateway
        </Option>
      </Section>
      {props.gateway.gatewayType === GatewayType.S3 && (
        <Section>
          <Option
            onSelect={async () => {
              const {roleArn} = await getS3GatewayInstaller();

              runModal(ViewBucketInfoModal, {
                roleArn,
                onRequestClose: props.refetch,
                gatewayName: props.gateway.name,
                bucketName: props.gateway.customerBucket || '',
              });
            }}
          >
            View bucket info
          </Option>
        </Section>
      )}
    </Menu>
  );
}

interface InstrumentGatewaysPageProps {
  instrumentGateways: Array<InstrumentGateway> | null;
  refetch: () => void;
}

function InstrumentGatewaysPage(props: InstrumentGatewaysPageProps): JSX.Element {
  setWindowTitle(`Benchling Gateways`);
  const {instrumentGateways} = props;
  if (instrumentGateways == null) {
    return <CenteredHorizontalThrobber />;
  }
  return (
    <div className="settingsPage">
      <div
        className="settingsPage-contentWrapper"
        {...getTestProps({component: 'settingsPageContainer', element: 'root'})}
      >
        <div className="u-lr-split">
          <div style={{width: '70%'}}>
            <Header1>Benchling Gateways</Header1>
            <p className="help-block">
              A Benchling gateway serves as a home for all of your instrument software adapters. Up to 3
              gateways from multiple tenants can be installed on a single PC. A gateway installer only needs
              to be downloaded on the PC once, but an installer token must be generated per tenant to install
              gateways from multiple tenants.
            </p>
          </div>
          <div style={{float: 'right'}}>
            {!BenchlingConfig.INSTRUMENTS_ENABLE_BASE_INSTALLER_FLOW && (
              <Button
                buttonStyle={ButtonStyle.PRIMARY}
                onClick={() => {
                  runModal(CreateInstrumentGatewayModal, {
                    existingGateways: instrumentGateways,
                    onRequestSubmit: (name) => {
                      withLoadingScreen(createGateway(name), 'Setting up instrument gateway...').then(() => {
                        props.refetch();
                      });
                    },
                  });
                }}
                size={ButtonSize.SMALL}
                text="Create instrument gateway"
              />
            )}
            {BenchlingConfig.INSTRUMENTS_ENABLE_BASE_INSTALLER_FLOW && (
              <div className="buttonGroup">
                <Button
                  buttonStyle={ButtonStyle.PRIMARY}
                  size={ButtonSize.SMALL}
                  type="button"
                  isDropdown={true}
                  text="Download"
                />
                <ul className="dropdown-menu pull-right">
                  {BenchlingConfig.INSTRUMENTS_ENABLE_S3_GATEWAY && (
                    <>
                      <li className={css(styles.dropdownSectionHeader)}>Cloud Gateway</li>
                      <li>
                        <a
                          onClick={async () => {
                            const {roleArn} = await getS3GatewayInstaller();

                            runModal(CreateS3GatewayModal, {
                              roleArn,
                              onRequestClose: props.refetch,
                              onRequestSubmit: async ({gatewayName, bucketName}) => {
                                await withLoadingScreen(
                                  // Trim surrounding whitespace from bucket name
                                  createGateway(gatewayName, 'S3', {customer_bucket: bucketName.trim()}),
                                  'Setting up instrument gateway...'
                                );

                                props.refetch();
                              },
                            });
                          }}
                        >
                          Configure S3 gateway
                        </a>
                      </li>
                      <li className={css(styles.dropdownSectionHeader)}>Local Gateway</li>
                    </>
                  )}
                  <li>
                    <a
                      onClick={async () => {
                        const {downloadUrl} = await getGatewayInstaller('windows');
                        redirectForDownload(downloadUrl);
                        runModal(DownloadGatewayModal, {onRequestClose: props.refetch});
                      }}
                    >
                      Download gateway
                    </a>
                  </li>
                  {BenchlingConfig.INSTRUMENTS_ENABLE_LINUX_INSTALLER_FLOW && (
                    <li>
                      <a
                        onClick={async () => {
                          const {downloadUrl} = await getGatewayInstaller('linux');
                          redirectForDownload(downloadUrl);
                          runModal(DownloadGatewayModal, {onRequestClose: props.refetch});
                        }}
                      >
                        Download linux gateway
                      </a>
                    </li>
                  )}

                  <li>
                    <a
                      onClick={async () => {
                        const {downloadUrl} = await createInstallerTokenFile();
                        redirectForDownload(downloadUrl);
                        runModal(DownloadTokenModal, {onRequestClose: props.refetch});
                      }}
                    >
                      Generate new installer token
                    </a>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
        <Table numRows={instrumentGateways.length} overflowVisible={true} className={css(styles.table)}>
          <Column
            header="Name"
            renderCell={(rowIndex: number) => (
              <Cell>
                <span className={classNames({faded: instrumentGateways[rowIndex].isArchived})}>
                  {instrumentGateways[rowIndex].name}
                </span>
              </Cell>
            )}
          />
          <Column
            header="Type"
            renderCell={(rowIndex: number) => (
              <Cell>
                <span>{GATEWAY_TYPE_TO_DISPLAY[instrumentGateways[rowIndex].gatewayType]}</span>
              </Cell>
            )}
          />
          <Column
            header="Status"
            renderCell={(rowIndex: number) => <StatusCell gateway={instrumentGateways[rowIndex]} />}
          />
          <Column
            header="Version"
            renderCell={(rowIndex: number) => <VersionCell gateway={instrumentGateways[rowIndex]} />}
          />
          {!BenchlingConfig.INSTRUMENTS_ENABLE_BASE_INSTALLER_FLOW && (
            <Column
              header="Download installer"
              renderCell={(rowIndex: number) => (
                <DownloadInstallerCell gateway={instrumentGateways[rowIndex]} />
              )}
            />
          )}
          <Column
            header="Upgrade"
            renderCell={(rowIndex: number) => (
              <UpgradeCell gateway={instrumentGateways[rowIndex]} refetch={props.refetch} />
            )}
          />
          {BenchlingConfig.INSTRUMENTS_ENABLE_BASE_INSTALLER_FLOW ? (
            <Column
              header="Actions"
              renderCell={(rowIndex: number) => (
                <ActionCell gateway={instrumentGateways[rowIndex]} refetch={props.refetch} />
              )}
            />
          ) : (
            <Column
              header="Archive"
              renderCell={(rowIndex: number) => (
                <ArchiveCell gateway={instrumentGateways[rowIndex]} refetch={props.refetch} />
              )}
            />
          )}
        </Table>
      </div>
    </div>
  );
}

export default function InstrumentGatewaysPageDataFetcher(): JSX.Element {
  const [instrumentGateways, setInstrumentGateways] = useState(null as Array<InstrumentGateway> | null);
  const refetch = (): void => {
    listGateways().then(({gateways}) => {
      setInstrumentGateways(naturalSortByKey(gateways, 'name'));
    });
  };
  useOnMount(refetch);
  if (instrumentGateways == null) {
    return <CenteredHorizontalThrobber />;
  }
  return <InstrumentGatewaysPage instrumentGateways={instrumentGateways} refetch={refetch} />;
}

export const styles = StyleSheet.create({
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  table: {
    marginTop: '35px',
  },
  infoBanner: {
    marginBottom: '10px',
  },
  statusIcon: {
    marginRight: '6px',
  },
  versionWarningIcon: {
    color: Colors.ORANGE,
    marginLeft: '6px',
  },
  healthy: {
    color: Colors.GREEN,
  },
  unhealthy: {
    color: Colors.RED_LIGHT,
  },
  unregistered: {
    color: Colors.GRAY_DARK,
  },
  dropdownSectionHeader: {
    ...FONT_PROXIMA_10,
    color: Colors.GRAY_DARKER,
    fontStyle: 'italic',
    height: '23px',
    cursor: 'default',
    padding: '3px 10px',
  },
  roleArnBox: {
    display: 'flex',
    gridTemplateColumns: '4fr 1fr',
  },
  roleArnField: {
    flex: 1,
  },
  copyButton: {
    width: '97px',
    height: '28px',
    marginLeft: '10px',
    alignSelf: 'flex-end',
    fontSize: '12px',
    alignItems: 'center',
    boxSizing: 'border-box',
    padding: '0px 16px',
  },
});
